import { useState, useRef } from 'react';
import { Upload, X, Loader2, FileAudio, Image as ImageIcon, Check } from 'lucide-react';

/**
 * FileUpload component for uploading files to Cloudinary
 * Supports both image and audio file uploads with progress indicator
 */
export default function FileUpload({
  type = 'image', // 'image' or 'audio'
  value = '',
  onChange,
  folder = 'uploads',
  placeholder = '',
  accept,
  maxSizeMB = 50,
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(value ? 'url' : 'upload'); // 'upload' or 'url'
  const fileInputRef = useRef(null);

  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

  const defaultAccept = type === 'image'
    ? 'image/jpeg,image/png,image/webp,image/gif'
    : 'audio/*,.mp3,.wav,.ogg,.m4a,.aac,.flac,.mpeg,.mpga';

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    const maxBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      setError(`File too large. Maximum size is ${maxSizeMB}MB`);
      return;
    }

    // Validate file type - be more lenient for audio files
    if (type === 'image') {
      const validImageTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      if (!validImageTypes.includes(file.type)) {
        setError('Invalid file type. Accepted: JPG, PNG, WebP, GIF');
        return;
      }
    } else if (type === 'audio') {
      // Accept any audio type or common audio extensions
      const isAudio = file.type.startsWith('audio/') ||
        /\.(mp3|wav|ogg|m4a|aac|flac|mpeg|mpga)$/i.test(file.name);
      if (!isAudio) {
        setError('Invalid file type. Accepted: MP3, WAV, OGG, M4A, AAC, FLAC, MPEG');
        return;
      }
    }

    // Check Cloudinary configuration
    if (!cloudName || !uploadPreset) {
      setError('Cloudinary not configured. Please check your environment variables.');
      console.error('Missing REACT_APP_CLOUDINARY_CLOUD_NAME or REACT_APP_CLOUDINARY_UPLOAD_PRESET');
      console.error('Current values:', { cloudName, uploadPreset });
      return;
    }

    setIsUploading(true);
    setError(null);
    setProgress(0);

    try {
      // Prepare form data for Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', uploadPreset);
      formData.append('folder', folder);

      // Use different resource type for audio
      const resourceType = type === 'audio' ? 'video' : 'image'; // Cloudinary uses 'video' for audio files

      // Upload to Cloudinary with progress tracking
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const pct = Math.round((event.loaded / event.total) * 100);
          setProgress(pct);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          onChange(response.secure_url);
          setIsUploading(false);
          setProgress(100);
        } else {
          console.error('Upload failed:', xhr.responseText);
          setError('Upload failed. Please try again.');
          setIsUploading(false);
        }
      });

      xhr.addEventListener('error', () => {
        console.error('Upload error');
        setError('Upload failed. Please try again.');
        setIsUploading(false);
      });

      xhr.open('POST', `https://api.cloudinary.com/v1_1/${cloudName}/${resourceType}/upload`);
      xhr.send(formData);

    } catch (err) {
      console.error('Upload error:', err);
      setError('Upload failed. Please try again.');
      setIsUploading(false);
    }
  };

  const handleUrlChange = (e) => {
    onChange(e.target.value);
  };

  const clearFile = () => {
    onChange('');
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const Icon = type === 'image' ? ImageIcon : FileAudio;

  return (
    <div className="space-y-2">
      {/* Mode Toggle */}
      <div className="flex gap-2 mb-2">
        <button
          type="button"
          onClick={() => setMode('upload')}
          className={`px-3 py-1 text-sm rounded-md transition-colors ${
            mode === 'upload'
              ? 'bg-orange-500 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          <Upload className="w-3 h-3 inline mr-1" />
          Upload
        </button>
        <button
          type="button"
          onClick={() => setMode('url')}
          className={`px-3 py-1 text-sm rounded-md transition-colors ${
            mode === 'url'
              ? 'bg-orange-500 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          URL
        </button>
      </div>

      {mode === 'upload' ? (
        <div className="relative">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept={accept || defaultAccept}
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Upload area or preview */}
          {value && !isUploading ? (
            <div className="relative border border-gray-700 rounded-lg overflow-hidden bg-gray-800">
              {type === 'image' ? (
                <div className="relative aspect-video">
                  <img
                    src={value}
                    alt="Upload preview"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-3 py-1 bg-white/90 text-black text-sm rounded-md hover:bg-white"
                    >
                      Change
                    </button>
                    <button
                      type="button"
                      onClick={clearFile}
                      className="px-3 py-1 bg-red-500 text-white text-sm rounded-md hover:bg-red-600"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <Check className="w-6 h-6 text-green-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium truncate">Audio file uploaded</p>
                    <p className="text-xs text-gray-400 truncate">{value}</p>
                  </div>
                  <button
                    type="button"
                    onClick={clearFile}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          ) : isUploading ? (
            <div className="border border-gray-700 rounded-lg p-6 bg-gray-800">
              <div className="flex items-center justify-center gap-3 mb-3">
                <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
                <span className="text-white">Uploading... {progress}%</span>
              </div>
              <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full border-2 border-dashed border-gray-700 rounded-lg p-8 hover:border-orange-500/50 transition-colors group"
            >
              <div className="flex flex-col items-center gap-2 text-gray-400 group-hover:text-orange-400 transition-colors">
                <Icon className="w-10 h-10" />
                <span className="font-medium">
                  Click to upload {type === 'image' ? 'image' : 'audio file'}
                </span>
                <span className="text-xs text-gray-500">
                  {type === 'image' ? 'JPG, PNG, WebP, GIF' : 'MP3, WAV, OGG, MPEG'} • Max {maxSizeMB}MB
                </span>
              </div>
            </button>
          )}
        </div>
      ) : (
        <input
          type="url"
          value={value}
          onChange={handleUrlChange}
          placeholder={placeholder || `Enter ${type} URL...`}
          className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white placeholder:text-gray-500 focus:outline-none focus:border-orange-500"
        />
      )}

      {/* Error message */}
      {error && (
        <p className="text-sm text-red-400 flex items-center gap-1">
          <X className="w-4 h-4" />
          {error}
        </p>
      )}
    </div>
  );
}
